import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useCartProvider } from '../VNItems/hooks/VNCartContext'
import { getCartItemIndex, getItemModifiedQuantity, getItemQuantity } from '../selectors/cart'
import { store } from '../stores'
import useCartItem from '../VNItems/hooks/useCartItem'
import { getItemsForCurrentMenu } from '../selectors/items'
import { hasRequiredModifiers } from '../utils/orderUtils'
import { ToastManager } from '../utils/toastManager'
import { APPLICATION_STRINGS } from '../strings'

export const useBarcodeScanner = ({ throttlingRef, throttlingActiveRef }) => {
  const { isCartLocked } = useCartProvider()
  const menuItems = useSelector(getItemsForCurrentMenu)
  const { addNonRegularItem, upsertItem } = useCartItem({
    throttlingRef,
    throttlingActiveRef,
    isInCartRef: { current: false },
    quantitiesRef: { current: undefined },
  })

  const getItemWithBarcode = useCallback(
    (barcode) => {
      return Object.keys(menuItems?.byId || {}).find((itemId) => {
        const item = menuItems.byId[itemId] || {}
        return item?.barcode === barcode
      })
    },
    [menuItems],
  )

  const addScannedItem = useCallback(
    (scannedBarcode) => {
      if (isCartLocked()) return

      const state = store.getState()
      const itemBarcode = getItemWithBarcode(scannedBarcode)

      if (!itemBarcode) {
        ToastManager.error(APPLICATION_STRINGS.BARCODE_NOT_RECOGNIZED)
        return
      }

      const item = menuItems?.byId?.[itemBarcode] || {}
      const cartQuantity = getItemQuantity(state, item?.id)
      const cartQuantityWithModifiers = getItemModifiedQuantity(state, item?.id)
      const sumQuantity = cartQuantityWithModifiers + cartQuantity
      const orderMaxAmount = parseInt(item?.orderMaxAmount ?? 999)
      const cartIndex = getCartItemIndex(state, item?.id)
      const hasReqMods = hasRequiredModifiers(item)
      const price = item?.defaultPriceInCents !== null ? `$${(item?.defaultPriceInCents / 100)?.toFixed(2)}` : '--'

      if (item.variants?.[0]?.inventoryState === 'unavailable') return
      if (sumQuantity >= orderMaxAmount) {
        ToastManager.error(APPLICATION_STRINGS.MAX_AMOUNT_REACHED)
        return
      }
      if (price === '--' || hasReqMods) {
        addNonRegularItem({ itemId: item.id, isAlcohol: item.isAlcohol })
        return
      }

      upsertItem({
        itemId: item.id,
        isAlcohol: item.isAlcohol,
        max: item.orderMaxAmount,
        hasRequiredModifiers: item.hasRequiredModifiers,
        quantity: sumQuantity + 1,
        isAlreadyInCart: cartIndex !== -1,
        cartIndex,
      })
    },
    [isCartLocked, getItemWithBarcode, menuItems, addNonRegularItem, upsertItem],
  )

  return { addScannedItem }
}
